/* Mockup v6 — Volledig drag-and-drop bouwer.
 *  - Secties verplaatsen (⋮⋮ in sectiekop)
 *  - INDIVIDUELE VELDEN sleepbaar binnen elke sectie (⋮⋮ links van elke rij)
 *  - Per veld: breedte instellen (S=33% / M=66% / L=100%)
 *  - Vakjes-grootte globaal Klein/Normaal/Groot
 *  - Kleur per sectie
 *  - Alles bewaard in localStorage
 */

import { useState, useEffect } from 'react';
import { Sparkle, Plus, Barcode, MagnifyingGlass, ArrowSquareOut, DotsSixVertical } from '@phosphor-icons/react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DEMO = {
  art_code: '60273000', omschrijving: 'CD60/27 Plafond profiel 8st/pk 224st/672L/M L=3,0M',
  foto_url: 'https://images.unsplash.com/photo-1565608087341-404b25492fee?w=200&h=200&fit=crop',
  categorie: 'Profiel', ean: '5412345678901', toon_ean: false,
  aankoopprijs: 322.88, btw: 21,
  prijzen: { basic: 443.52, standard: 443.52, premium: 487.87, bulk: 399.17, kassa: 421.35 },
  eenheid: 'pack', voorraad: 99, min_voorraad: 0, gewicht_kg: 373.3, volume_m3: 0,
  familie: 'CD60/27', eenheid_aantal: 224, eenheid_label: 'L/M', toon_eenheid_prijs: true,
  actief: true,
  hs_code: '73089059', hs_niveau: 'CN-8 · EU invoer',
  hs_beschrijving: 'Andere constructiedelen van ijzer of staal',
  hs_motivatie: 'Het betreft metalen profielen (CD60/27) gebruikt als constructiedelen voor gipsplafonds.',
  oorsprong_land: 'CN',
  import_tarief: { invoerrecht_pct: 0, btw_pct: 21, antidumping: 'Geen actieve maatregelen.' },
  leveranciers: [
    { naam: 'Alpha Steel NV', aankoop: 322.88, termijn: 14, moq: 100, actief: true },
    { naam: 'Beta Metals BV', aankoop: 331.50, termijn: 7, moq: 50, actief: true },
  ],
};

// ──────────────────────────── VELD-DEFINITIES ─────────────────────────────
// Elke veld krijgt een unieke id + sectie + render-functie.
// De render-functie ontvangt {widthPct} en geeft de rij-inhoud terug.
const winst = DEMO.prijzen.basic - DEMO.aankoopprijs;
const marge = (winst / DEMO.prijzen.basic * 100);

function inpStyle(widthPct) { return { width: `${widthPct}%` }; }

const VELDEN = [
  // ══ IDENTIFICATIE ══
  { id: 'categorie', sectie: 'identificatie', label: 'Categorie', defaultWidth: 'M',
    render: (w) => (<select className="mini-input" defaultValue={DEMO.categorie} style={inpStyle(w)}>
      <option>Profiel</option><option>Accessoires</option><option>Werktuig</option>
    </select>) },
  { id: 'art_code', sectie: 'identificatie', label: 'Art. code', defaultWidth: 'S',
    render: (w) => <input className="mini-input font-mono" defaultValue={DEMO.art_code} style={inpStyle(w)} /> },
  { id: 'omschrijving', sectie: 'identificatie', label: 'Omschrijving', defaultWidth: 'L',
    render: (w) => <input className="mini-input" defaultValue={DEMO.omschrijving} style={inpStyle(w)} /> },
  { id: 'ean', sectie: 'identificatie', label: 'EAN', defaultWidth: 'M',
    render: (w) => (<div className="flex gap-1.5" style={inpStyle(w)}>
      <input className="mini-input font-mono flex-1" defaultValue={DEMO.ean} />
      <button className="mini-btn"><Sparkle size={10} weight="duotone" /> 🎲</button>
      <button className="mini-btn"><Barcode size={11} weight="duotone" /></button>
    </div>) },
  { id: 'ean_zichtbaar', sectie: 'identificatie', label: 'EAN op doc', defaultWidth: 'L',
    render: () => <div className="flex items-center gap-2"><MockToggle checked={DEMO.toon_ean} /><span className="text-[11px] text-slate-500">Toon EAN op factuur/offerte/leverbon</span></div> },
  { id: 'foto', sectie: 'identificatie', label: 'Foto', defaultWidth: 'L',
    render: () => (<div className="flex items-center gap-3">
      <img src={DEMO.foto_url} alt="" className="w-14 h-14 rounded border border-slate-200 object-cover flex-shrink-0" />
      <div className="flex gap-1.5">
        <button className="mini-btn">📷 Upload</button>
        <button className="mini-btn">🖱️ Kopieer van …</button>
        <button className="mini-btn text-red-600">✕</button>
      </div>
    </div>) },
  { id: 'familie', sectie: 'identificatie', label: 'Familie', defaultWidth: 'M',
    render: (w) => <input className="mini-input" defaultValue={DEMO.familie} style={inpStyle(w)} /> },

  // ══ PRIJZEN ══
  { id: 'aankoop', sectie: 'prijzen', label: 'Aankoop (excl.)', defaultWidth: 'S',
    render: (w) => (<div className="flex items-center gap-2" style={inpStyle(w)}>
      <span className="text-[11px] text-slate-400 font-mono">€</span>
      <input className="mini-input font-mono flex-1" defaultValue={DEMO.aankoopprijs.toFixed(2)} />
    </div>) },
  { id: 'btw', sectie: 'prijzen', label: 'BTW %', defaultWidth: 'S',
    render: (w) => (<select className="mini-input" defaultValue={DEMO.btw} style={inpStyle(w)}>
      <option value="0">0%</option><option value="6">6%</option><option value="12">12%</option><option value="21">21%</option>
    </select>) },
  { id: 'verkoop_excl', sectie: 'prijzen', label: 'Verkoop excl. BTW', defaultWidth: 'L',
    render: () => (<div className="grid grid-cols-5 gap-1.5">
      {['basic','standard','premium','bulk','kassa'].map(g => (
        <div key={g}>
          <div className={`text-[9px] font-bold uppercase text-center mb-0.5 ${g === 'basic' ? 'text-emerald-700' : 'text-slate-400'}`}>{g === 'basic' ? '⭐ Basic' : g}</div>
          <input className={`mini-input font-mono text-center ${g === 'basic' ? 'border-emerald-300 bg-emerald-50/40 font-bold' : ''}`} defaultValue={DEMO.prijzen[g].toFixed(2)} />
        </div>
      ))}
    </div>) },
  { id: 'verkoop_incl', sectie: 'prijzen', label: 'Verkoop incl. BTW', defaultWidth: 'L',
    render: () => (<div className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] text-slate-400 font-mono">€</span>
      <input className="mini-input font-mono w-32 bg-slate-50 cursor-not-allowed text-slate-700 font-bold" value={(DEMO.prijzen.basic * (1 + DEMO.btw/100)).toFixed(2)} readOnly />
      <span className="text-[10px] text-slate-500">= € {DEMO.prijzen.basic.toFixed(2)} excl. + {DEMO.btw}% BTW</span>
    </div>) },
  { id: 'kpi_marge', sectie: 'prijzen', label: 'Winst / Marge', defaultWidth: 'L',
    render: () => (<div className="grid grid-cols-3 gap-1.5">
      <div className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-[11px]"><span className="text-emerald-700 font-bold uppercase text-[9px]">Winst</span><div className="font-bold text-emerald-800">€ {winst.toFixed(2)}</div></div>
      <div className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-[11px]"><span className="text-emerald-700 font-bold uppercase text-[9px]">Marge</span><div className="font-bold text-emerald-800">{marge.toFixed(1)}%</div></div>
      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-[11px]"><span className="text-slate-600 font-bold uppercase text-[9px]">Kostprijs incl.</span><div className="font-bold text-slate-800">€ {DEMO.aankoopprijs.toFixed(2)}</div></div>
    </div>) },

  // ══ VOORRAAD ══
  { id: 'verkoopeenheid', sectie: 'voorraad', label: 'Verkoopeenheid', defaultWidth: 'M',
    render: (w) => (<select className="mini-input" defaultValue={DEMO.eenheid} style={inpStyle(w)}>
      <option value="stuk">Per stuk</option><option value="pack">Per pak/pallet</option><option value="kg">Per kg</option><option value="m2">Per m²</option>
    </select>) },
  { id: 'voorraad', sectie: 'voorraad', label: 'Voorraad', defaultWidth: 'M',
    render: () => (<div className="flex items-center gap-4">
      <input className="mini-input font-mono w-24" defaultValue={DEMO.voorraad} />
      <span className="text-[11px] text-slate-500">Min.</span>
      <input className="mini-input font-mono w-24" defaultValue={DEMO.min_voorraad} />
    </div>) },
  { id: 'gewicht_volume', sectie: 'voorraad', label: 'Gewicht / Volume', defaultWidth: 'L',
    render: () => (<div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-1"><input className="mini-input font-mono w-24" defaultValue={DEMO.gewicht_kg} /><span className="text-[11px] text-slate-500">kg</span></div>
      <div className="flex items-center gap-1"><input className="mini-input font-mono w-24" defaultValue={DEMO.volume_m3 || '0.000'} /><span className="text-[11px] text-slate-500">m³</span></div>
      <span className="text-[10px] text-slate-400 italic">voor CMR</span>
    </div>) },
  { id: 'meterprijs', sectie: 'voorraad', label: 'Meterprijs', hint: "Optioneel · '€ X/L.M.' op documenten", defaultWidth: 'L',
    render: () => (<div className="flex items-center gap-2 flex-wrap">
      <input className="mini-input font-mono w-24" defaultValue={DEMO.eenheid_aantal} />
      <input className="mini-input w-20" defaultValue={DEMO.eenheid_label} />
      <MockToggle checked={DEMO.toon_eenheid_prijs} />
      <span className="text-[11px] text-slate-500">Tonen</span>
      <span className="text-[11px] text-emerald-700 font-semibold ml-auto">≈ € {(DEMO.prijzen.basic / DEMO.eenheid_aantal).toFixed(2)}/{DEMO.eenheid_label}</span>
    </div>) },

  // ══ DOUANE ══
  { id: 'hs_code', sectie: 'douane', label: 'HS-code', defaultWidth: 'L',
    render: () => (<div className="flex gap-1.5 items-center flex-wrap">
      <input className="mini-input font-mono w-48" defaultValue={DEMO.hs_code} />
      <button className="mini-btn !px-2 !text-white" style={{ background: 'linear-gradient(135deg,#2563EB,#4F46E5)' }}><Sparkle size={10} weight="duotone" /> 🤖 AI</button>
      <span className="inline-flex px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] font-bold rounded uppercase">{DEMO.hs_niveau}</span>
    </div>) },
  { id: 'oorsprong', sectie: 'douane', label: 'Oorsprong', defaultWidth: 'S',
    render: (w) => <input className="mini-input font-mono" defaultValue={DEMO.oorsprong_land} style={inpStyle(w)} /> },
  { id: 'ai_beschrijving', sectie: 'douane', label: 'Beschrijving (AI)', defaultWidth: 'L',
    render: () => (<div className="bg-slate-50 border-l-4 border-blue-400 rounded-r px-2.5 py-1.5 text-[11px]">
      <div className="font-semibold text-slate-800">{DEMO.hs_beschrijving}</div>
      <div className="italic text-slate-600 mt-0.5">💡 {DEMO.hs_motivatie}</div>
    </div>) },
  { id: 'invoerrechten', sectie: 'douane', label: 'Invoerrechten', defaultWidth: 'L',
    render: () => (<div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-0.5"><span className="text-[9px] text-blue-700 font-bold uppercase">Invoerrecht</span><span className="font-bold text-blue-800">{DEMO.import_tarief.invoerrecht_pct}%</span></div>
      <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-0.5"><span className="text-[9px] text-blue-700 font-bold uppercase">BTW invoer</span><span className="font-bold text-blue-800">{DEMO.import_tarief.btw_pct}%</span></div>
      <span className="text-[10px] text-slate-500 italic flex-1 min-w-0 truncate">{DEMO.import_tarief.antidumping}</span>
      <button className="mini-btn"><MagnifyingGlass size={10} /> Actualiseer</button>
    </div>) },

  // ══ LEVERANCIERS ══
  { id: 'lev_tabel', sectie: 'leveranciers', label: 'Leveranciers', defaultWidth: 'L',
    render: () => (<div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <tr><th className="text-left py-1">Leverancier</th><th className="text-right py-1">Aankoop</th><th className="text-right py-1">Termijn</th><th className="text-right py-1">MOQ</th><th className="text-center py-1 w-10">Actief</th></tr>
        </thead>
        <tbody>
          {DEMO.leveranciers.map((l, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-b-0">
              <td className="py-1.5">{i === 0 && <span className="inline-block px-1 py-0.5 bg-emerald-100 text-emerald-700 text-[8px] font-bold rounded mr-1 uppercase">🏆</span>}<span className="font-medium text-slate-900">{l.naam}</span></td>
              <td className="py-1.5 text-right font-mono text-slate-900">€ {l.aankoop.toFixed(2)}</td>
              <td className="py-1.5 text-right text-slate-600">{l.termijn} d</td>
              <td className="py-1.5 text-right text-slate-600">{l.moq}</td>
              <td className="py-1.5 text-center"><MockToggle checked={l.actief} small /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="mt-2 mini-btn"><Plus size={10} weight="bold" /> Leverancier toevoegen</button>
    </div>) },

  // ══ VARIANTEN ══
  { id: 'var_module', sectie: 'varianten', label: 'Module', defaultWidth: 'L',
    render: () => (<div className="flex items-center gap-3 flex-wrap">
      <span className="text-[11px] text-purple-700 font-semibold">🎨 Niet actief</span>
      <button className="mini-btn !bg-purple-600 !text-white hover:!bg-purple-700">Activeer</button>
      <span className="text-[10px] text-slate-500 italic">bv. kleur × maat voor kleding</span>
    </div>) },

  // ══ STATUS ══
  { id: 'actief', sectie: 'status', label: 'Actief product', defaultWidth: 'S',
    render: () => <MockToggle checked={DEMO.actief} large /> },
];

const DEFAULT_SECTIES = [
  { key: 'identificatie', label: 'Identificatie', color: 'slate' },
  { key: 'prijzen',       label: 'Prijszetting',  color: 'emerald' },
  { key: 'voorraad',      label: 'Voorraad & logistiek', color: 'slate' },
  { key: 'douane',        label: 'Douane & import', color: 'blue' },
  { key: 'leveranciers',  label: 'Voorkeurs-leveranciers', color: 'slate' },
  { key: 'varianten',     label: 'Varianten / maten', color: 'purple' },
  { key: 'status',        label: 'Status', color: 'slate' },
];

const KLEUREN = [
  { key: 'slate',   label: 'Grijs',  bg: '#F1F5F9', border: '#CBD5E1', text: '#475569' },
  { key: 'emerald', label: 'Groen',  bg: '#ECFDF5', border: '#A7F3D0', text: '#047857' },
  { key: 'blue',    label: 'Blauw',  bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' },
  { key: 'purple',  label: 'Paars',  bg: '#F5F3FF', border: '#DDD6FE', text: '#6D28D9' },
  { key: 'amber',   label: 'Geel',   bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' },
  { key: 'rose',    label: 'Rood',   bg: '#FFF1F2', border: '#FECDD3', text: '#BE123C' },
];

const STORAGE_KEY = 'artikel-mockup-v6-config';
const VAK_GROOTTES = { S: { input: 24, font: 11 }, M: { input: 28, font: 12 }, L: { input: 34, font: 13 } };
const WIDTH_PCT = { S: 33, M: 66, L: 100 };

// Default volgorde + width per veld, opgebouwd uit VELDEN-array
function buildDefaultConfig() {
  const ordering = {}; // {sectie: [veldId,...]}
  const widths = {};   // {veldId: 'S'|'M'|'L'}
  for (const v of VELDEN) {
    if (!ordering[v.sectie]) ordering[v.sectie] = [];
    ordering[v.sectie].push(v.id);
    widths[v.id] = v.defaultWidth;
  }
  return { ordering, widths };
}

export default function ArtikelMockupPage() {
  const [secties, setSecties] = useState(DEFAULT_SECTIES);
  const [grootte, setGrootte] = useState('M');
  const [bouwmodus, setBouwmodus] = useState(true);
  const [veldOrdering, setVeldOrdering] = useState(() => buildDefaultConfig().ordering);
  const [veldWidths, setVeldWidths] = useState(() => buildDefaultConfig().widths);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved?.secties) setSecties(saved.secties);
      if (saved?.grootte) setGrootte(saved.grootte);
      if (saved?.veldOrdering) setVeldOrdering(saved.veldOrdering);
      if (saved?.veldWidths) setVeldWidths(saved.veldWidths);
    } catch (e) { /* eerste keer */ }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ secties, grootte, veldOrdering, veldWidths }));
  }, [secties, grootte, veldOrdering, veldWidths]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onSectieDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSecties(prev => arrayMove(prev, prev.findIndex(s => s.key === active.id), prev.findIndex(s => s.key === over.id)));
  };

  const onVeldDragEnd = (sectieKey) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setVeldOrdering(prev => {
      const arr = prev[sectieKey] || [];
      const oldIdx = arr.indexOf(active.id);
      const newIdx = arr.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return { ...prev, [sectieKey]: arrayMove(arr, oldIdx, newIdx) };
    });
  };

  const wijzigKleur = (idx, color) => {
    const arr = [...secties]; arr[idx] = { ...arr[idx], color };
    setSecties(arr);
  };
  const wijzigVeldWidth = (veldId, width) => {
    setVeldWidths(prev => ({ ...prev, [veldId]: width }));
  };
  const reset = () => {
    if (window.confirm('Reset alle aanpassingen?')) {
      const def = buildDefaultConfig();
      setSecties(DEFAULT_SECTIES);
      setGrootte('M');
      setVeldOrdering(def.ordering);
      setVeldWidths(def.widths);
    }
  };

  const grootteStyle = VAK_GROOTTES[grootte];

  return (
    <div className="min-h-screen bg-slate-100 pb-32" data-testid="artikel-mockup-page">
      {/* Bouwer-toolbar */}
      <div className="sticky top-0 z-50 bg-amber-50 border-b-2 border-amber-300 px-4 py-2.5 flex items-center gap-3 flex-wrap shadow-sm">
        <span className="font-bold text-amber-900 text-sm">🛠️ Bouwer-modus</span>
        <button
          onClick={() => setBouwmodus(b => !b)}
          type="button"
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-200 hover:bg-amber-300 text-amber-900"
        >
          {bouwmodus ? '👁 Verberg toolbars' : '✏️ Toon toolbars'}
        </button>
        <span className="text-amber-700 text-xs">·</span>
        <span className="text-amber-800 text-xs inline-flex items-center gap-1">
          <DotsSixVertical size={14} weight="bold" /> Sleep secties OF velden
        </span>
        <span className="text-amber-700 text-xs">·</span>
        <div className="inline-flex items-center gap-1 text-xs">
          <span className="text-amber-800 font-semibold">Vakjes:</span>
          {['S', 'M', 'L'].map(g => (
            <button key={g} type="button" onClick={() => setGrootte(g)}
              className={`px-2 py-0.5 rounded text-xs font-bold border ${grootte === g ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'}`}
              data-testid={`size-${g}`}
            >{g === 'S' ? 'Klein' : g === 'M' ? 'Normaal' : 'Groot'}</button>
          ))}
        </div>
        <button onClick={reset} type="button" className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-white hover:bg-red-50 text-red-700 border border-red-200">↺ Reset</button>
      </div>

      <header className="flex items-center justify-between gap-3 px-5 py-2 bg-white border-b border-slate-300">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <img src={DEMO.foto_url} alt="" className="w-10 h-10 rounded border border-slate-200 object-cover flex-shrink-0" />
          <div className="flex items-center gap-1.5 text-slate-700">
            <Plus size={14} weight="bold" />
            <span className="font-semibold">Product bewerken</span>
            <span className="text-slate-400 mx-1">·</span>
            <span className="font-mono text-sm font-bold text-slate-800">{DEMO.art_code}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="inline-flex px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] font-bold rounded uppercase tracking-wider">{DEMO.hs_niveau}</span>
          <a href="/artikels" className="text-slate-500 hover:text-slate-700 text-xs inline-flex items-center gap-1"><ArrowSquareOut size={12} /> Lijst</a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto mt-4 space-y-3" style={{ '--inp-h': `${grootteStyle.input}px`, '--inp-fs': `${grootteStyle.font}px` }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSectieDragEnd}>
          <SortableContext items={secties.map(s => s.key)} strategy={verticalListSortingStrategy}>
            {secties.map((sectie, idx) => (
              <SectieKaart
                key={sectie.key}
                sectie={sectie}
                bouwmodus={bouwmodus}
                onColor={(c) => wijzigKleur(idx, c)}
              >
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onVeldDragEnd(sectie.key)}>
                  <SortableContext items={veldOrdering[sectie.key] || []} strategy={verticalListSortingStrategy}>
                    {(veldOrdering[sectie.key] || []).map(veldId => {
                      const veld = VELDEN.find(v => v.id === veldId);
                      if (!veld) return null;
                      const w = veldWidths[veldId] || veld.defaultWidth;
                      return (
                        <SortableRij
                          key={veldId}
                          veldId={veldId}
                          label={veld.label}
                          hint={veld.hint}
                          bouwmodus={bouwmodus}
                          width={w}
                          onWidth={(nw) => wijzigVeldWidth(veldId, nw)}
                        >
                          {veld.render(WIDTH_PCT[w])}
                        </SortableRij>
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </SectieKaart>
            ))}
          </SortableContext>
        </DndContext>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-2.5 bg-white border-t border-slate-300" style={{ boxShadow: '0 -4px 12px rgba(0,0,0,0.05)' }}>
        <div className="text-[11px] text-slate-500">🛠️ <span className="font-semibold">Bouwer v6</span> · sleep secties EN velden · breedte per veld instelbaar</div>
        <div className="flex gap-2">
          <button className="mini-btn">Annuleren</button>
          <button className="mini-btn !bg-emerald-600 !text-white hover:!bg-emerald-700 !border-emerald-600 px-4">💾 Bijwerken</button>
        </div>
      </footer>

      <style>{`
        .mini-input { height: var(--inp-h, 28px); padding: 0 8px; border: 1px solid #CBD5E1; border-radius: 4px; background: #fff; font-size: var(--inp-fs, 12px); color: #0F172A; outline: none; transition: border-color 0.15s; }
        .mini-input:focus { border-color: #10B981; box-shadow: 0 0 0 2px rgba(16,185,129,0.1); }
        .mini-btn { height: calc(var(--inp-h, 28px) - 2px); padding: 0 8px; border: 1px solid #CBD5E1; border-radius: 4px; background: #fff; color: #334155; font-size: calc(var(--inp-fs, 12px) - 1px); font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 2px; }
        .mini-btn:hover { background: #F8FAFC; border-color: #94A3B8; }
        .rij-label-w { width: 160px; }
      `}</style>
    </div>
  );
}

/* ══════════════ Sortable sectie-kaart ══════════════ */
function SectieKaart({ sectie, bouwmodus, onColor, children }) {
  const kleur = KLEUREN.find(k => k.key === sectie.color) || KLEUREN[0];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sectie.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 100 : 'auto',
    boxShadow: isDragging ? '0 12px 30px rgba(15,23,42,0.18)' : undefined,
    borderColor: kleur.border,
  };
  return (
    <section ref={setNodeRef} style={style} className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ background: kleur.bg, borderColor: kleur.border, color: kleur.text }}>
        {bouwmodus && (
          <button {...attributes} {...listeners} type="button"
            className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-white/50 cursor-grab active:cursor-grabbing flex-shrink-0"
            title="Sleep om sectie te verplaatsen" data-testid={`drag-sectie-${sectie.key}`}
          ><DotsSixVertical size={14} weight="bold" /></button>
        )}
        <span className="text-[11px] font-bold uppercase tracking-wider flex-1">{sectie.label}</span>
        {bouwmodus && (
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-medium opacity-70">Kleur:</span>
            {KLEUREN.map(k => (
              <button key={k.key} onClick={() => onColor(k.key)} title={k.label} type="button"
                className={`w-4 h-4 rounded-full transition ${sectie.color === k.key ? 'ring-2 ring-offset-1 ring-slate-700 scale-110' : 'border border-white/60 hover:scale-110'}`}
                style={{ background: k.border }}
              />
            ))}
          </div>
        )}
      </div>
      <div>{children}</div>
    </section>
  );
}

/* ══════════════ Sortable rij (per veld) met breedte-knoppen ══════════════ */
function SortableRij({ veldId, label, hint, bouwmodus, width, onWidth, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: veldId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 50 : 'auto',
    boxShadow: isDragging ? '0 8px 20px rgba(15,23,42,0.12)' : undefined,
    background: isDragging ? '#FEF9C3' : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 last:border-b-0">
      {bouwmodus && (
        <button {...attributes} {...listeners} type="button"
          className="w-5 h-5 inline-flex items-center justify-center rounded hover:bg-slate-100 cursor-grab active:cursor-grabbing flex-shrink-0 text-slate-400 hover:text-slate-700"
          title="Sleep dit veld om volgorde te wijzigen" data-testid={`drag-veld-${veldId}`}
        ><DotsSixVertical size={11} weight="bold" /></button>
      )}
      <div className="rij-label-w flex-shrink-0">
        <label className="text-[12px] font-medium text-slate-700">{label}</label>
        {hint && <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">{hint}</div>}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
      {bouwmodus && (
        <div className="flex items-center gap-0.5 flex-shrink-0" data-testid={`width-toolbar-${veldId}`}>
          {['S', 'M', 'L'].map(w => (
            <button key={w} type="button"
              onClick={() => onWidth(w)}
              title={`Breedte: ${w === 'S' ? 'Smal (33%)' : w === 'M' ? 'Medium (66%)' : 'Breed (100%)'}`}
              className={`w-6 h-5 text-[9px] font-bold rounded border transition ${width === w ? 'bg-amber-500 text-white border-amber-600' : 'bg-white text-slate-500 border-slate-200 hover:border-amber-400'}`}
              data-testid={`width-${veldId}-${w}`}
            >{w}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function MockToggle({ checked, small, large }) {
  const sizes = { small: { h: 16, w: 28, dot: 12, tr: 14 }, default: { h: 18, w: 32, dot: 14, tr: 16 }, large: { h: 22, w: 38, dot: 16, tr: 20 } };
  const s = large ? sizes.large : small ? sizes.small : sizes.default;
  return (
    <div className={`relative inline-flex items-center rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-300'}`} style={{ height: s.h, width: s.w }}>
      <span className="inline-block rounded-full bg-white shadow-sm transition-transform" style={{ height: s.dot, width: s.dot, transform: checked ? `translateX(${s.tr}px)` : 'translateX(2px)' }} />
    </div>
  );
}
